import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore, type UserRole } from '@/stores/authStore';
import { isSupabaseConfigured } from '@/lib/supabase';
import { isDemoEnabled } from '@/lib/dataMode';
import { useTranslation } from 'react-i18next';
import { ShieldX, ArrowLeft } from 'lucide-react';
import type { ReactNode } from 'react';

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles: UserRole[];
}

export function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { session, profile, isLoading } = useAuthStore();
  const { t } = useTranslation();
  const location = useLocation();

  // Demo mode — either Supabase isn't configured at all (local dev), or the
  // current user is a demo profile (production "Try Demo" flow on Vercel).
  // SECURITY: demo users are restricted to 'owner' routes only — they can
  // never access admin/inspector/pilot dashboards to prevent anonymous
  // visitors from seeing role-gated internal workflows.
  const isDemoProfile = profile?.id?.startsWith('demo-user') ?? false;

  if (isDemoProfile && isDemoEnabled()) {
    // Demo mode: only allow if 'owner' is in allowedRoles — block admin/inspector/pilot
    if (!allowedRoles.includes('owner')) {
      return <Navigate to="/owner/dashboard" replace />;
    }
    return <>{children}</>;
  }

  // Missing backend configuration is never an authorization mechanism.
  if (!isSupabaseConfigured) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[var(--bg)]">
        <div className="w-10 h-10 rounded-full border-2 border-[var(--border2)] border-t-[var(--green)] animate-spin" />
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (profile && !allowedRoles.includes(profile.role)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[var(--bg)] px-6">
        <div className="max-w-md w-full text-center">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-[color-mix(in_srgb,var(--red)_10%,transparent)] border border-[color-mix(in_srgb,var(--red)_20%,transparent)] flex items-center justify-center mb-6">
            <ShieldX size={32} className="text-[var(--red)]" />
          </div>
          <h1 className="text-2xl font-[var(--font-main)] font-bold text-[var(--text)] mb-2">
            {t('errors.forbidden')}
          </h1>
          <p className="text-sm text-[var(--text2)] mb-8">{t('errors.forbiddenMessage')}</p>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[color-mix(in_srgb,var(--green)_10%,transparent)] text-[var(--green)] border border-[var(--border2)] text-sm font-medium hover:bg-[color-mix(in_srgb,var(--green)_15%,transparent)] transition-colors"
          >
            <ArrowLeft size={16} />
            {t('errors.goHome')}
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
